import { Widget } from "@/modules";

/**
 * Widget type placement order (first in array = top of grid)
 * New widgets of each type will be placed after rows of higher-priority types
 * Types not in this array will be placed at the end
 */
export const WIDGET_TYPE_ORDER: Widget["type"][] = ["budget-tickers-group-by-product", "ticker"] as const;

export const GRID_CONFIG = {
    /**
     * Maximum number of columns in the grid
     */
    MAX_COLUMNS: 5,

    /**
     * Default gap between grid items (in pixels)
     */
    GAP_SIZE: 24,

    MIN_COL_SPAN: 1,
    AUTO_REMOVE_EMPTY_ROWS: true,

    /**
     * Strategy used to visually compact the grid when widgets are hidden.
     * Render-time only — does not affect persistence. Swap to other registered
     * strategies as new client rules arrive.
     */
    COMPACTION_STRATEGY: "immediateNextRowFill",
} as const;

/**
 * Per-state switches that control which widget conditions count as "hidden"
 * for the compaction module. Flip any flag to false to revert that state to
 * its pre-compaction render (e.g. set errorState=false to bring back the
 * "Unable to load data / Retry" card instead of collapsing the slot).
 */
export const COMPACTION_HIDDEN_TRIGGERS = {
    /** Empty data, empty arrays, hide-on-incomplete-buildout, etc. */
    emptyData: true,
    /** Widget data fetch errored out. */
    errorState: true,
    /** Future-proofing: widget types that render their own NoDataView card. */
    noDataView: true,
} as const;

export type CompactionHiddenTrigger = keyof typeof COMPACTION_HIDDEN_TRIGGERS;

/* DEFAULT WIDGET COLSSPAN on creation  */
export const WIDGET_SIZES: Record<Widget["type"], { colsSpan: number }> = {
    chart: { colsSpan: 5 },
    ticker: { colsSpan: 1 },
    table: { colsSpan: 5 },
    iframe: { colsSpan: 5 },
    "data-sources": { colsSpan: 5 },
    "budget-tickers-group-by-product": { colsSpan: 5 },
    "competitor-table": { colsSpan: 5 },
    "consumer-data": { colsSpan: 5 },
    "creative-gallery": { colsSpan: 5 },
    "social-impact-posts": { colsSpan: 5 },
    "top-offers": { colsSpan: 5 },
    "table-map": { colsSpan: 5 },
    "audience-map": { colsSpan: 5 },
    combined: { colsSpan: 5 },
    "content-schedule": { colsSpan: 5 },
} as const;

export type WidgetType = keyof typeof WIDGET_SIZES;
